import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';

import { ProviderType } from 'src/modules/user/models/user';
import { ContactUsModal } from 'src/shared/components/ContactUsModal';
import { color, font, layout, mediaQueries } from 'src/shared/styles/variables';
import { ProviderFragment } from '../documents/getMyProvidersQuery.generated';
import { ContactUsBanner } from './ContactUsBanner';

import { ProviderCard } from './ProviderCard';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 30,
    maxWidth: layout.containerBiggerWidth,
    maxHeight: 'auto',
    alignItems: 'center',
    [mediaQueries.sm]: {
        marginTop: 50,
    },
});

const Container = styled.div({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
});

const Title = styled.h3({
    color: color.pickledBluewood,
    ...font.black(font.size.extraLarge),
    marginBottom: 0,
});

const Subtitle = styled.h5({
    color: color.steelGray,
    marginTop: 10,
    marginBottom: 10,
    ...font.roman(font.size.mediumExtended),
});

interface ProvidersListWrapperProps {
    centerContent: boolean;
}

const ProvidersListWrapper = styled.ul<ProvidersListWrapperProps>(props => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: props.centerContent ? 'center' : 'space-between',
    maxWidth: 1236,
    marginTop: 32,
    padding: 0,
    [mediaQueries.lg]: {
        flexDirection: 'column',
        justifyContent: 'center',
    },
}));

export interface Props {
    selectedProvider?: string;
    onProviderPress: (uuid: string) => void;
    providerTypeToDisplay?: ProviderType;
    headerText: string;
    subTitleText: string;
}

export interface AssignedProviderSelectionProps extends Props {
    providers: ProviderFragment[];
}

export enum ProviderSelectionTestID {
    ShowMoreButton = 'ProviderSelection.ShowMoreButton',
    GoToTopButton = 'ProviderSelection.GoToTopButton',
    ContactUsSection = 'ProviderSelection.ContactUsSection',
    ContactUsModal = 'ProviderSelection.ContactUsModal',
    NoProviderModal = 'ProviderSelection.NoProviderModal',
}

export const AssignedProviderSelection: React.FC<AssignedProviderSelectionProps> = ({
    providers,
    onProviderPress,
    selectedProvider,
    headerText,
    subTitleText,
}) => {
    const { explorePageConnectWithSupport } = useFlags();
    const [modalVisible, setModalVisible] = useState(false);

    return (
        <Container>
            <Wrapper>
                <Title>{headerText}</Title>
                <Subtitle>{subTitleText}</Subtitle>

                <ProvidersListWrapper centerContent>
                    {providers.slice(0, 9).map(provider => (
                        <ProviderCard
                            key={provider.uuid}
                            provider={provider}
                            loading={selectedProvider === provider.uuid}
                            onPress={onProviderPress}
                        />
                    ))}
                </ProvidersListWrapper>

                {explorePageConnectWithSupport && (
                    <ContactUsBanner
                        alreadyMatchedMode
                        onClick={() => setModalVisible(true)}
                        hasDeprecatedProviders={providers.length > 0}
                    />
                )}

                <ContactUsModal
                    dataTestID={ProviderSelectionTestID.ContactUsModal}
                    visible={modalVisible}
                    onToggle={() => setModalVisible(isModalVisible => !isModalVisible)}
                    tags={[`selection_flow_contact_us_modal`]}
                    helpDesk
                />
            </Wrapper>
        </Container>
    );
};
